// Comprehensive VA Condition Rating Estimation Questions
// Based on official VA Schedule of Ratings and CFR 38

export interface EstimationQuestion {
  id: string;
  question: string;
  type: 'yes_no' | 'multiple_choice' | 'severity_scale';
  options?: string[];
  ratingMapping: {
    answer: string;
    suggestedRating: number | number[]; // Single rating or range
  }[];
}

export interface SecondaryCondition {
  name: string;
  description: string;
  typicalRatings: number[];
}

export interface ConditionQuestionnaire {
  conditionName: string;
  questions: EstimationQuestion[];
  secondaryConditions: SecondaryCondition[];
}

export const conditionQuestionnaires: { [key: string]: ConditionQuestionnaire } = {
  "tinnitus": {
    conditionName: "Tinnitus",
    questions: [
      {
        id: "tinnitus_1",
        question: "Do you experience constant or recurring ringing, buzzing, or other sounds in your ears?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: 10 },
          { answer: "no", suggestedRating: 0 }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Hearing Loss",
        description: "Often occurs alongside tinnitus",
        typicalRatings: [0, 10, 20, 30, 40, 50, 60, 100]
      },
      {
        name: "Sleep Apnea",
        description: "Can be aggravated by tinnitus-related sleep disturbance",
        typicalRatings: [0, 30, 50, 100]
      }
    ]
  },
  "ptsd": {
    conditionName: "PTSD",
    questions: [
      {
        id: "ptsd_1",
        question: "Do your symptoms interfere with your ability to work or maintain employment?",
        type: "multiple_choice",
        options: ["Not at all", "Occasionally", "Frequently", "Unable to work"],
        ratingMapping: [
          { answer: "Not at all", suggestedRating: [0, 10] },
          { answer: "Occasionally", suggestedRating: [10, 30] },
          { answer: "Frequently", suggestedRating: [50, 70] },
          { answer: "Unable to work", suggestedRating: 100 }
        ]
      },
      {
        id: "ptsd_2",
        question: "Do you experience panic attacks or severe anxiety?",
        type: "multiple_choice",
        options: ["Never", "Rarely (less than monthly)", "Weekly", "Daily or near-daily"],
        ratingMapping: [
          { answer: "Never", suggestedRating: 0 },
          { answer: "Rarely (less than monthly)", suggestedRating: [10, 30] },
          { answer: "Weekly", suggestedRating: [30, 50] },
          { answer: "Daily or near-daily", suggestedRating: [50, 70] }
        ]
      },
      {
        id: "ptsd_3",
        question: "Do you have difficulty maintaining relationships with family or friends?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 70] },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      },
      {
        id: "ptsd_4",
        question: "Do you experience suicidal thoughts or have you required hospitalization for mental health?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [70, 100] },
          { answer: "no", suggestedRating: [0, 50] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Depression",
        description: "Often co-occurs with PTSD",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Anxiety Disorder",
        description: "Frequently linked to PTSD symptoms",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Sleep Apnea",
        description: "Can be secondary to PTSD-related sleep disturbance",
        typicalRatings: [0, 30, 50, 100]
      },
      {
        name: "Migraine Headaches",
        description: "Can be triggered or worsened by PTSD",
        typicalRatings: [0, 10, 30, 50]
      }
    ]
  },
  "ibs": {
    conditionName: "IBS",
    questions: [
      {
        id: "ibs_1",
        question: "How often do you experience abdominal pain or discomfort?",
        type: "multiple_choice",
        options: ["Rarely", "Occasionally (monthly)", "Frequently (weekly)", "Constantly (daily)"],
        ratingMapping: [
          { answer: "Rarely", suggestedRating: 0 },
          { answer: "Occasionally (monthly)", suggestedRating: 10 },
          { answer: "Frequently (weekly)", suggestedRating: [10, 30] },
          { answer: "Constantly (daily)", suggestedRating: [30, 60] }
        ]
      },
      {
        id: "ibs_2",
        question: "Do your symptoms limit your daily activities or work?",
        type: "multiple_choice",
        options: ["No limitation", "Slight limitation", "Moderate limitation", "Severe limitation"],
        ratingMapping: [
          { answer: "No limitation", suggestedRating: 0 },
          { answer: "Slight limitation", suggestedRating: 10 },
          { answer: "Moderate limitation", suggestedRating: [10, 30] },
          { answer: "Severe limitation", suggestedRating: [30, 60] }
        ]
      },
      {
        id: "ibs_3",
        question: "Do you require frequent medical care or medications for your condition?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 60] },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "GERD",
        description: "Gastroesophageal reflux disease often associated with IBS",
        typicalRatings: [10, 30, 60]
      },
      {
        name: "Anxiety Disorder",
        description: "GI issues can trigger or worsen anxiety",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Depression",
        description: "Chronic GI symptoms can contribute to depression",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "back condition": {
    conditionName: "Back Condition",
    questions: [
      {
        id: "back_1",
        question: "Does your back pain affect your daily activities or mobility?",
        type: "multiple_choice",
        options: ["No effect", "Mild effect (occasional discomfort)", "Moderate effect (limits some activities)", "Severe effect (major limitation)"],
        ratingMapping: [
          { answer: "No effect", suggestedRating: 0 },
          { answer: "Mild effect (occasional discomfort)", suggestedRating: 10 },
          { answer: "Moderate effect (limits some activities)", suggestedRating: [10, 40] },
          { answer: "Severe effect (major limitation)", suggestedRating: [40, 100] }
        ]
      },
      {
        id: "back_2",
        question: "Do you require assistive devices (cane, walker) or are you unable to work due to your back?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [40, 100] },
          { answer: "no", suggestedRating: [0, 40] }
        ]
      },
      {
        id: "back_3",
        question: "Can you bend forward and touch your toes?",
        type: "multiple_choice",
        options: ["Yes, easily", "With difficulty", "Cannot reach knees", "Cannot bend forward at all"],
        ratingMapping: [
          { answer: "Yes, easily", suggestedRating: 0 },
          { answer: "With difficulty", suggestedRating: 10 },
          { answer: "Cannot reach knees", suggestedRating: [20, 40] },
          { answer: "Cannot bend forward at all", suggestedRating: [40, 100] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Sciatica",
        description: "Nerve pain radiating down legs from back condition",
        typicalRatings: [10, 20, 40, 60, 80]
      },
      {
        name: "Knee Condition",
        description: "Altered gait from back pain can cause knee issues",
        typicalRatings: [0, 10, 20, 30, 60]
      },
      {
        name: "Hip Condition",
        description: "Back conditions often affect hip mobility",
        typicalRatings: [10, 20, 30, 40, 50, 60]
      }
    ]
  },
  "knee condition": {
    conditionName: "Knee Condition",
    questions: [
      {
        id: "knee_1",
        question: "Does your knee pain or instability limit your ability to walk or stand?",
        type: "multiple_choice",
        options: ["No limitation", "Slight limitation (short distances)", "Moderate limitation (assistance needed)", "Severe limitation (cannot walk)"],
        ratingMapping: [
          { answer: "No limitation", suggestedRating: 0 },
          { answer: "Slight limitation (short distances)", suggestedRating: 10 },
          { answer: "Moderate limitation (assistance needed)", suggestedRating: [20, 30] },
          { answer: "Severe limitation (cannot walk)", suggestedRating: 60 }
        ]
      },
      {
        id: "knee_2",
        question: "Does your knee lock, give way, or feel unstable?",
        type: "multiple_choice",
        options: ["Never", "Occasionally", "Frequently", "Constantly"],
        ratingMapping: [
          { answer: "Never", suggestedRating: 0 },
          { answer: "Occasionally", suggestedRating: 10 },
          { answer: "Frequently", suggestedRating: [20, 30] },
          { answer: "Constantly", suggestedRating: [30, 60] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Back Condition",
        description: "Altered gait from knee issues can cause back problems",
        typicalRatings: [10, 20, 30, 40, 50, 100]
      },
      {
        name: "Hip Condition",
        description: "Compensating for knee pain affects hip",
        typicalRatings: [10, 20, 30, 40, 50, 60]
      },
      {
        name: "Plantar Fasciitis",
        description: "Altered walking mechanics can cause foot problems",
        typicalRatings: [0, 10, 20, 30, 40]
      }
    ]
  },
  "shoulder condition": {
    conditionName: "Shoulder Condition",
    questions: [
      {
        id: "shoulder_1",
        question: "Can you raise your arm above shoulder level?",
        type: "multiple_choice",
        options: ["Yes, no problem", "Yes, with pain", "Only to shoulder level", "Cannot raise at all"],
        ratingMapping: [
          { answer: "Yes, no problem", suggestedRating: 0 },
          { answer: "Yes, with pain", suggestedRating: 10 },
          { answer: "Only to shoulder level", suggestedRating: [20, 30] },
          { answer: "Cannot raise at all", suggestedRating: 40 }
        ]
      },
      {
        id: "shoulder_2",
        question: "Does your shoulder pain limit your work or daily activities?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [20, 40] },
          { answer: "no", suggestedRating: [0, 20] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Neck Condition",
        description: "Shoulder issues often relate to neck problems",
        typicalRatings: [10, 20, 30, 40, 50]
      },
      {
        name: "Back Condition",
        description: "Shoulder problems can cause compensatory back strain",
        typicalRatings: [10, 20, 30, 40, 50, 100]
      },
      {
        name: "Carpal Tunnel Syndrome",
        description: "Shoulder nerve issues can extend to wrist",
        typicalRatings: [0, 10, 20, 30, 50, 70]
      }
    ]
  },
  "migraine headaches": {
    conditionName: "Migraine Headaches",
    questions: [
      {
        id: "migraine_1",
        question: "How often do you experience prostrating migraine attacks (severe enough to require bed rest)?",
        type: "multiple_choice",
        options: ["Less than monthly", "1-2 times per month", "3-4 times per month", "More than once per week"],
        ratingMapping: [
          { answer: "Less than monthly", suggestedRating: 0 },
          { answer: "1-2 times per month", suggestedRating: 10 },
          { answer: "3-4 times per month", suggestedRating: 30 },
          { answer: "More than once per week", suggestedRating: 50 }
        ]
      },
      {
        id: "migraine_2",
        question: "Do your migraines prevent you from working or performing daily tasks?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 50] },
          { answer: "no", suggestedRating: [0, 10] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Tinnitus",
        description: "Migraines can cause or worsen tinnitus",
        typicalRatings: [10]
      },
      {
        name: "PTSD",
        description: "Chronic pain can trigger or worsen PTSD symptoms",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Sleep Apnea",
        description: "Migraines can disrupt sleep patterns",
        typicalRatings: [0, 30, 50, 100]
      }
    ]
  },
  "migraines": {
    conditionName: "Migraines",
    questions: [
      {
        id: "migraine_1",
        question: "How often do you experience prostrating migraine attacks (severe enough to require bed rest)?",
        type: "multiple_choice",
        options: ["Less than monthly", "1-2 times per month", "3-4 times per month", "More than once per week"],
        ratingMapping: [
          { answer: "Less than monthly", suggestedRating: 0 },
          { answer: "1-2 times per month", suggestedRating: 10 },
          { answer: "3-4 times per month", suggestedRating: 30 },
          { answer: "More than once per week", suggestedRating: 50 }
        ]
      },
      {
        id: "migraine_2",
        question: "Do your migraines prevent you from working or performing daily tasks?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 50] },
          { answer: "no", suggestedRating: [0, 10] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Tinnitus",
        description: "Migraines can cause or worsen tinnitus",
        typicalRatings: [10]
      },
      {
        name: "PTSD",
        description: "Chronic pain can trigger or worsen PTSD symptoms",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Sleep Apnea",
        description: "Migraines can disrupt sleep patterns",
        typicalRatings: [0, 30, 50, 100]
      }
    ]
  },
  "sleep apnea": {
    conditionName: "Sleep Apnea",
    questions: [
      {
        id: "sleep_apnea_1",
        question: "Do you use a CPAP machine or other breathing assistance device?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: 50 },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      },
      {
        id: "sleep_apnea_2",
        question: "Do you experience daytime sleepiness that affects your daily functioning?",
        type: "multiple_choice",
        options: ["No", "Mild sleepiness", "Moderate sleepiness", "Severe persistent sleepiness"],
        ratingMapping: [
          { answer: "No", suggestedRating: 0 },
          { answer: "Mild sleepiness", suggestedRating: 10 },
          { answer: "Moderate sleepiness", suggestedRating: 30 },
          { answer: "Severe persistent sleepiness", suggestedRating: 50 }
        ]
      },
      {
        id: "sleep_apnea_3",
        question: "Do you have respiratory failure or require a tracheostomy?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: 100 },
          { answer: "no", suggestedRating: [0, 50] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Hypertension",
        description: "Sleep apnea commonly causes or worsens high blood pressure",
        typicalRatings: [0, 10, 20, 40, 60]
      },
      {
        name: "Depression",
        description: "Sleep disruption contributes to depression",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Anxiety Disorder",
        description: "Poor sleep quality worsens anxiety",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "depression": {
    conditionName: "Depression",
    questions: [
      {
        id: "depression_1",
        question: "Do your symptoms interfere with work, school, or social activities?",
        type: "multiple_choice",
        options: ["Not at all", "Occasionally", "Frequently", "Unable to function"],
        ratingMapping: [
          { answer: "Not at all", suggestedRating: 0 },
          { answer: "Occasionally", suggestedRating: [10, 30] },
          { answer: "Frequently", suggestedRating: [50, 70] },
          { answer: "Unable to function", suggestedRating: 100 }
        ]
      },
      {
        id: "depression_2",
        question: "Do you experience severe mood swings or have difficulty maintaining relationships?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [50, 70] },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      },
      {
        id: "depression_3",
        question: "Have you had suicidal thoughts or required hospitalization?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [70, 100] },
          { answer: "no", suggestedRating: [0, 50] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Anxiety Disorder",
        description: "Depression and anxiety often co-occur",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Sleep Apnea",
        description: "Depression affects sleep quality",
        typicalRatings: [0, 30, 50, 100]
      },
      {
        name: "PTSD",
        description: "Depression can be related to traumatic experiences",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "anxiety disorder": {
    conditionName: "Anxiety Disorder",
    questions: [
      {
        id: "anxiety_1",
        question: "How often do you experience panic attacks?",
        type: "multiple_choice",
        options: ["Never", "Less than monthly", "Weekly", "Daily or near-daily"],
        ratingMapping: [
          { answer: "Never", suggestedRating: 0 },
          { answer: "Less than monthly", suggestedRating: [10, 30] },
          { answer: "Weekly", suggestedRating: [30, 50] },
          { answer: "Daily or near-daily", suggestedRating: [50, 70] }
        ]
      },
      {
        id: "anxiety_2",
        question: "Does anxiety prevent you from working or maintaining relationships?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [50, 100] },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      },
      {
        id: "anxiety_3",
        question: "Do you have specific phobias (e.g., acrophobia, social phobia) that limit your daily life?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 70] },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Depression",
        description: "Anxiety and depression frequently occur together",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "PTSD",
        description: "Anxiety can be linked to traumatic events",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "IBS",
        description: "Anxiety commonly causes or worsens GI symptoms",
        typicalRatings: [0, 10, 30, 60]
      }
    ]
  },
  "hearing loss": {
    conditionName: "Hearing Loss",
    questions: [
      {
        id: "hearing_1",
        question: "How severe is your hearing loss?",
        type: "multiple_choice",
        options: ["Mild (some difficulty)", "Moderate (hearing aid helpful)", "Severe (cannot hear normal conversation)", "Complete deafness"],
        ratingMapping: [
          { answer: "Mild (some difficulty)", suggestedRating: 10 },
          { answer: "Moderate (hearing aid helpful)", suggestedRating: [20, 40] },
          { answer: "Severe (cannot hear normal conversation)", suggestedRating: [50, 60] },
          { answer: "Complete deafness", suggestedRating: 100 }
        ]
      },
      {
        id: "hearing_2",
        question: "Is the hearing loss in both ears?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [20, 100] },
          { answer: "no", suggestedRating: [0, 30] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Tinnitus",
        description: "Hearing loss often accompanied by tinnitus",
        typicalRatings: [10]
      },
      {
        name: "Depression",
        description: "Hearing loss can lead to social isolation and depression",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      },
      {
        name: "Anxiety Disorder",
        description: "Communication difficulties can increase anxiety",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "sciatica": {
    conditionName: "Sciatica",
    questions: [
      {
        id: "sciatica_1",
        question: "Do you experience numbness, weakness, or severe pain radiating down your leg?",
        type: "multiple_choice",
        options: ["Mild occasional pain", "Moderate pain with some weakness", "Severe pain with significant weakness", "Cannot walk without assistance"],
        ratingMapping: [
          { answer: "Mild occasional pain", suggestedRating: 10 },
          { answer: "Moderate pain with some weakness", suggestedRating: [20, 40] },
          { answer: "Severe pain with significant weakness", suggestedRating: [60, 80] },
          { answer: "Cannot walk without assistance", suggestedRating: 80 }
        ]
      },
      {
        id: "sciatica_2",
        question: "Do you have visible muscle atrophy (wasting) in your leg?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [40, 80] },
          { answer: "no", suggestedRating: [0, 40] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Back Condition",
        description: "Sciatica originates from back/spine issues",
        typicalRatings: [10, 20, 30, 40, 50, 100]
      },
      {
        name: "Knee Condition",
        description: "Altered gait from sciatica can cause knee problems",
        typicalRatings: [0, 10, 20, 30, 60]
      },
      {
        name: "Hip Condition",
        description: "Hip problems can accompany or cause sciatica",
        typicalRatings: [10, 20, 30, 40, 50, 60]
      }
    ]
  },
  "plantar fasciitis": {
    conditionName: "Plantar Fasciitis",
    questions: [
      {
        id: "plantar_1",
        question: "Does foot pain affect your ability to walk or stand?",
        type: "multiple_choice",
        options: ["Mild pain, no limitation", "Moderate pain with some limitation", "Severe pain, significant limitation", "Cannot walk without severe pain"],
        ratingMapping: [
          { answer: "Mild pain, no limitation", suggestedRating: 10 },
          { answer: "Moderate pain with some limitation", suggestedRating: 20 },
          { answer: "Severe pain, significant limitation", suggestedRating: 30 },
          { answer: "Cannot walk without severe pain", suggestedRating: 40 }
        ]
      },
      {
        id: "plantar_2",
        question: "Do you have visible foot deformity or swelling?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [20, 40] },
          { answer: "no", suggestedRating: [0, 20] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Knee Condition",
        description: "Altered walking mechanics from foot pain affects knees",
        typicalRatings: [0, 10, 20, 30, 60]
      },
      {
        name: "Back Condition",
        description: "Compensatory gait changes cause back strain",
        typicalRatings: [10, 20, 30, 40, 50, 100]
      },
      {
        name: "Hip Condition",
        description: "Foot problems affect hip alignment and function",
        typicalRatings: [10, 20, 30, 40, 50, 60]
      }
    ]
  },
  "carpal tunnel syndrome": {
    conditionName: "Carpal Tunnel Syndrome",
    questions: [
      {
        id: "carpal_1",
        question: "Do you experience numbness, tingling, or weakness in your hand?",
        type: "multiple_choice",
        options: ["Mild occasional symptoms", "Moderate symptoms with some weakness", "Severe symptoms with significant weakness", "Complete loss of hand function"],
        ratingMapping: [
          { answer: "Mild occasional symptoms", suggestedRating: 10 },
          { answer: "Moderate symptoms with some weakness", suggestedRating: [20, 30] },
          { answer: "Severe symptoms with significant weakness", suggestedRating: [50, 70] },
          { answer: "Complete loss of hand function", suggestedRating: 70 }
        ]
      },
      {
        id: "carpal_2",
        question: "Can you perform fine motor tasks (writing, buttoning, typing)?",
        type: "multiple_choice",
        options: ["No problem", "Some difficulty", "Significant difficulty", "Cannot perform"],
        ratingMapping: [
          { answer: "No problem", suggestedRating: 0 },
          { answer: "Some difficulty", suggestedRating: 10 },
          { answer: "Significant difficulty", suggestedRating: [20, 50] },
          { answer: "Cannot perform", suggestedRating: [50, 70] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Shoulder Condition",
        description: "Nerve issues can extend from shoulder to wrist",
        typicalRatings: [0, 10, 20, 30, 40]
      },
      {
        name: "Neck Condition",
        description: "Cervical spine problems can cause carpal tunnel symptoms",
        typicalRatings: [10, 20, 30, 40, 50]
      },
      {
        name: "Depression",
        description: "Chronic pain and functional limitation contribute to depression",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "hypertension": {
    conditionName: "Hypertension",
    questions: [
      {
        id: "hypertension_1",
        question: "What is your typical diastolic blood pressure reading (bottom number)?",
        type: "multiple_choice",
        options: ["Below 100", "100-109", "110-119", "120-129", "130 or higher"],
        ratingMapping: [
          { answer: "Below 100", suggestedRating: 0 },
          { answer: "100-109", suggestedRating: 10 },
          { answer: "110-119", suggestedRating: 20 },
          { answer: "120-129", suggestedRating: 40 },
          { answer: "130 or higher", suggestedRating: 60 }
        ]
      },
      {
        id: "hypertension_2",
        question: "What is your typical systolic blood pressure reading (top number)?",
        type: "multiple_choice",
        options: ["Below 160", "160-199", "200 or higher"],
        ratingMapping: [
          { answer: "Below 160", suggestedRating: 0 },
          { answer: "160-199", suggestedRating: 10 },
          { answer: "200 or higher", suggestedRating: 20 }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Sleep Apnea",
        description: "Sleep apnea commonly causes hypertension",
        typicalRatings: [0, 30, 50, 100]
      },
      {
        name: "Diabetes Type II",
        description: "Diabetes and hypertension often occur together",
        typicalRatings: [0, 10, 20, 40, 60, 100]
      },
      {
        name: "Anxiety Disorder",
        description: "Chronic anxiety can elevate blood pressure",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "diabetes type ii": {
    conditionName: "Diabetes Type II",
    questions: [
      {
        id: "diabetes_1",
        question: "How do you manage your diabetes?",
        type: "multiple_choice",
        options: ["Diet alone", "Oral medication", "Insulin injections", "Insulin + frequent hospitalizations"],
        ratingMapping: [
          { answer: "Diet alone", suggestedRating: 0 },
          { answer: "Oral medication", suggestedRating: 20 },
          { answer: "Insulin injections", suggestedRating: [40, 60] },
          { answer: "Insulin + frequent hospitalizations", suggestedRating: 100 }
        ]
      },
      {
        id: "diabetes_2",
        question: "Do you experience complications from diabetes (neuropathy, vision problems, kidney issues)?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [60, 100] },
          { answer: "no", suggestedRating: [0, 60] }
        ]
      },
      {
        id: "diabetes_3",
        question: "Do you need to regulate your activities and follow a restricted diet?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [40, 100] },
          { answer: "no", suggestedRating: [0, 20] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Hypertension",
        description: "Diabetes commonly causes high blood pressure",
        typicalRatings: [0, 10, 20, 40, 60]
      },
      {
        name: "Peripheral Neuropathy",
        description: "Diabetes causes nerve damage in extremities",
        typicalRatings: [10, 20, 40, 60]
      },
      {
        name: "Depression",
        description: "Managing chronic diabetes contributes to depression",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "gerd": {
    conditionName: "GERD",
    questions: [
      {
        id: "gerd_1",
        question: "How often do you experience heartburn or acid reflux?",
        type: "multiple_choice",
        options: ["Rarely", "Occasionally (weekly)", "Frequently (daily)", "Constant symptoms"],
        ratingMapping: [
          { answer: "Rarely", suggestedRating: 0 },
          { answer: "Occasionally (weekly)", suggestedRating: 10 },
          { answer: "Frequently (daily)", suggestedRating: 30 },
          { answer: "Constant symptoms", suggestedRating: 60 }
        ]
      },
      {
        id: "gerd_2",
        question: "Do you require daily medication or have complications like esophageal damage?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 60] },
          { answer: "no", suggestedRating: [0, 10] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "IBS",
        description: "GERD and IBS often occur together",
        typicalRatings: [0, 10, 30, 60]
      },
      {
        name: "Anxiety Disorder",
        description: "GI issues can worsen anxiety",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  },
  "hip condition": {
    conditionName: "Hip Condition",
    questions: [
      {
        id: "hip_1",
        question: "Does your hip pain limit your ability to walk or perform daily activities?",
        type: "multiple_choice",
        options: ["No limitation", "Slight limitation", "Moderate limitation (requires cane)", "Severe limitation (wheelchair needed)"],
        ratingMapping: [
          { answer: "No limitation", suggestedRating: 0 },
          { answer: "Slight limitation", suggestedRating: 10 },
          { answer: "Moderate limitation (requires cane)", suggestedRating: [20, 30] },
          { answer: "Severe limitation (wheelchair needed)", suggestedRating: [40, 60] }
        ]
      },
      {
        id: "hip_2",
        question: "Can you raise your leg to the side or bend at the hip?",
        type: "multiple_choice",
        options: ["Yes, normally", "With difficulty", "Very limited range", "Cannot move hip"],
        ratingMapping: [
          { answer: "Yes, normally", suggestedRating: 0 },
          { answer: "With difficulty", suggestedRating: 10 },
          { answer: "Very limited range", suggestedRating: [20, 40] },
          { answer: "Cannot move hip", suggestedRating: [50, 60] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Back Condition",
        description: "Hip problems often cause back pain",
        typicalRatings: [10, 20, 30, 40, 50, 100]
      },
      {
        name: "Knee Condition",
        description: "Altered gait from hip issues affects knees",
        typicalRatings: [0, 10, 20, 30, 60]
      }
    ]
  },
  "neck condition": {
    conditionName: "Neck Condition",
    questions: [
      {
        id: "neck_1",
        question: "Can you turn your head from side to side or look up and down?",
        type: "multiple_choice",
        options: ["Full range of motion", "Limited motion with pain", "Severely restricted motion", "Cannot move neck"],
        ratingMapping: [
          { answer: "Full range of motion", suggestedRating: 0 },
          { answer: "Limited motion with pain", suggestedRating: 10 },
          { answer: "Severely restricted motion", suggestedRating: [20, 30] },
          { answer: "Cannot move neck", suggestedRating: [40, 50] }
        ]
      },
      {
        id: "neck_2",
        question: "Do you experience radiating pain, numbness, or weakness in your arms?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [30, 50] },
          { answer: "no", suggestedRating: [0, 20] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Shoulder Condition",
        description: "Neck problems often extend to shoulders",
        typicalRatings: [0, 10, 20, 30, 40]
      },
      {
        name: "Migraine Headaches",
        description: "Neck issues can trigger headaches",
        typicalRatings: [0, 10, 30, 50]
      },
      {
        name: "Carpal Tunnel Syndrome",
        description: "Cervical spine problems can cause wrist symptoms",
        typicalRatings: [0, 10, 20, 30, 50, 70]
      }
    ]
  },
  "peripheral neuropathy": {
    conditionName: "Peripheral Neuropathy",
    questions: [
      {
        id: "peripheral_1",
        question: "Do you experience numbness, tingling, or pain in your hands or feet?",
        type: "multiple_choice",
        options: ["Mild occasional symptoms", "Moderate persistent symptoms", "Severe symptoms affecting function", "Complete loss of sensation"],
        ratingMapping: [
          { answer: "Mild occasional symptoms", suggestedRating: 10 },
          { answer: "Moderate persistent symptoms", suggestedRating: 20 },
          { answer: "Severe symptoms affecting function", suggestedRating: 40 },
          { answer: "Complete loss of sensation", suggestedRating: 60 }
        ]
      },
      {
        id: "peripheral_2",
        question: "Does neuropathy affect your ability to walk or use your hands?",
        type: "yes_no",
        ratingMapping: [
          { answer: "yes", suggestedRating: [40, 60] },
          { answer: "no", suggestedRating: [0, 20] }
        ]
      }
    ],
    secondaryConditions: [
      {
        name: "Diabetes Type II",
        description: "Often caused by diabetes",
        typicalRatings: [0, 10, 20, 40, 60, 100]
      },
      {
        name: "Depression",
        description: "Chronic nerve pain contributes to depression",
        typicalRatings: [0, 10, 30, 50, 70, 100]
      }
    ]
  }
};

export function getConditionQuestionnaire(conditionName: string): ConditionQuestionnaire | null {
  const normalizedName = conditionName.toLowerCase().trim();
  return conditionQuestionnaires[normalizedName] || null;
}

export function calculateEstimatedRating(
  questionnaire: ConditionQuestionnaire,
  answers: { [questionId: string]: string }
): { minRating: number; maxRating: number; confidence: string } {
  let totalMin = 0;
  let totalMax = 0;
  let questionCount = 0;

  questionnaire.questions.forEach(question => {
    const answer = answers[question.id];
    if (!answer) return;

    const mapping = question.ratingMapping.find(m => m.answer === answer);
    if (!mapping) return;

    questionCount++;

    if (Array.isArray(mapping.suggestedRating)) {
      totalMin += mapping.suggestedRating[0];
      totalMax += mapping.suggestedRating[1];
    } else {
      totalMin += mapping.suggestedRating;
      totalMax += mapping.suggestedRating;
    }
  });

  // Average the ratings
  const avgMin = questionCount > 0 ? totalMin / questionCount : 0;
  const avgMax = questionCount > 0 ? totalMax / questionCount : 0;

  // VA-approved ratings only: 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100
  const validRatings = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  
  // Snap to nearest valid VA rating
  const snapToValidRating = (rating: number): number => {
    const rounded = Math.round(rating);
    // Find closest valid rating
    return validRatings.reduce((prev, curr) => 
      Math.abs(curr - rounded) < Math.abs(prev - rounded) ? curr : prev
    );
  };

  const snappedMin = snapToValidRating(avgMin);
  const snappedMax = snapToValidRating(avgMax);

  // Ensure min is not greater than max
  const finalMin = Math.min(snappedMin, snappedMax);
  const finalMax = Math.max(snappedMin, snappedMax);

  // Determine confidence based on how many questions were answered
  const answerPercentage = (questionCount / questionnaire.questions.length) * 100;
  let confidence = "Low";
  if (answerPercentage >= 80) confidence = "High";
  else if (answerPercentage >= 50) confidence = "Moderate";

  return {
    minRating: finalMin,
    maxRating: finalMax,
    confidence
  };
}